/**
 * Enhanced SpecKit Dashboard - AI-Powered Project Management
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Alert,
  AlertTitle,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Fab,
  Checkbox,
  FormControlLabel,
  Snackbar
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  AutoFixHigh as AIIcon,
  Code as CodeIcon,
  Description as DocumentIcon,
  Task as TaskIcon,
  Analytics as AnalyticsIcon,
  ChecklistRtl as ChecklistIcon,
  Architecture as ArchitectureIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { logger } from '../utils/logger';

// Real SpecKit service interfaces for direct Docker integration
interface SpecKitServiceStatus {
  status: string;
  speckit_available: boolean;
  speckit_output: string;
}

interface SpecKitProjectInitResponse {
  success: boolean;
  message: string;
  files_generated?: string[];
}

interface SpecKitCommandResult {
  success: boolean;
  message: string;
  command?: string;
  output?: string;
  generated_at?: string;
  files_generated?: string[];
}

interface ProjectReadiness {
  ready: boolean;
  issues: string[];
  recommendations: string[];
}

interface SpecKitDashboardProps {
  projectId: number;
  projectName: string;
  onClose?: () => void;
}

// SpecKit service client for direct Docker API integration
class SpecKitDockerService {
  private baseUrl = 'http://localhost:8080';

  async checkHealth(): Promise<SpecKitServiceStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      logger.error('Failed to check SpecKit health', { error });
      throw error;
    }
  }

  async initializeProject(projectName: string): Promise<SpecKitProjectInitResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/projects/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: projectName,
          description: `AI-powered project: ${projectName}`,
          template: 'standard'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      logger.error('Failed to initialize SpecKit project', { error, projectName });
      throw error;
    }
  }

  async getProjectStatus(projectName: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${projectName}/status`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      logger.error('Failed to get project status', { error, projectName });
      throw error;
    }
  }

  async executeCommand(projectName: string, command: string, args?: any): Promise<SpecKitCommandResult> {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${projectName}/command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command,
          args: args || {},
          ignore_agent_tools: true // Skip Claude Code CLI tool for now
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: result.success || false,
        message: result.message || 'Command executed',
        command,
        output: result.output || result.message || '',
        generated_at: new Date().toISOString(),
        files_generated: result.files_generated || []
      };
    } catch (error) {
      logger.error('Failed to execute SpecKit command', { error, command, projectName });
      throw error;
    }
  }

  async validateProjectReadiness(projectId: number): Promise<ProjectReadiness> {
    try {
      // Check service health first
      const health = await this.checkHealth();
      const issues: string[] = [];
      const recommendations: string[] = [];

      if (!health.speckit_available) {
        issues.push('SpecKit CLI is not available');
        recommendations.push('Ensure SpecKit is properly installed in the container');
      }

      // For now, we'll consider the project ready if SpecKit is available
      // Additional validation logic can be added here
      
      return {
        ready: issues.length === 0,
        issues,
        recommendations: issues.length > 0 ? recommendations : ['Project is ready for SpecKit workflow execution']
      };
    } catch (error) {
      logger.error('Failed to validate project readiness', { error, projectId });
      return {
        ready: false,
        issues: ['Failed to connect to SpecKit service'],
        recommendations: ['Check if SpecKit Docker container is running on localhost:8080']
      };
    }
  }
}

const specKitDockerService = new SpecKitDockerService();

// Enhanced workflow progress tracking
interface WorkflowProgress {
  phase: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  result?: SpecKitCommandResult;
  error?: string;
  startTime?: Date;
  endTime?: Date;
  duration?: number;
}

const workflowSteps = [
  { 
    key: 'init', 
    label: 'Project Initialization', 
    icon: <ScienceIcon />, 
    description: 'Initialize SpecKit project structure',
    command: 'init'
  },
  { 
    key: 'constitution', 
    label: 'Constitution', 
    icon: <DocumentIcon />, 
    description: 'Generate project constitution and core principles',
    command: 'constitution'
  },
  { 
    key: 'specify', 
    label: 'Specification', 
    icon: <ArchitectureIcon />, 
    description: 'Create detailed technical specifications',
    command: 'specify'
  },
  { 
    key: 'plan', 
    label: 'Technical Plan', 
    icon: <CodeIcon />, 
    description: 'Develop implementation roadmap',
    command: 'plan'
  },
  { 
    key: 'tasks', 
    label: 'Task Breakdown', 
    icon: <TaskIcon />, 
    description: 'Generate actionable task list',
    command: 'tasks'
  },
  { 
    key: 'test', 
    label: 'Test Framework', 
    icon: <BuildIcon />, 
    description: 'Generate testing framework and procedures',
    command: 'test'
  }
];

// Custom TabPanel component for Material-UI v7
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const EnhancedSpecKitDashboard: React.FC<SpecKitDashboardProps> = ({
  projectId,
  projectName,
  onClose
}) => {
  const theme = useTheme();
  
  // State management for real SpecKit integration
  const [status, setStatus] = useState<SpecKitServiceStatus | null>(null);
  const [workflowProgress, setWorkflowProgress] = useState<WorkflowProgress[]>(
    workflowSteps.map(step => ({ 
      phase: step.key, 
      status: 'pending' as const,
      startTime: undefined,
      endTime: undefined,
      duration: undefined
    }))
  );
  const [isRunning, setIsRunning] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedResult, setSelectedResult] = useState<SpecKitCommandResult | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [requirements, setRequirements] = useState<string[]>([
    'Create a modern web application',
    'Follow VegaDigital development standards',
    'Implement comprehensive testing'
  ]);
  const [userStories, setUserStories] = useState<string[]>([
    'As a user, I want to manage projects efficiently',
    'As a developer, I want clear technical specifications',
    'As a team lead, I want detailed task breakdowns'
  ]);
  const [newRequirement, setNewRequirement] = useState('');
  const [newUserStory, setNewUserStory] = useState('');
  const [projectReadiness, setProjectReadiness] = useState<ProjectReadiness | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [projectInitialized, setProjectInitialized] = useState(false);

  // Initialize component
  useEffect(() => {
    initializeDashboard();
  }, [projectId]);

  const initializeDashboard = async () => {
    try {
      logger.info('Initializing SpecKit dashboard', { projectId });
      
      // Check SpecKit service health
      const specKitStatus = await specKitDockerService.checkHealth();
      setStatus(specKitStatus);
      
      // Validate project readiness
      const readiness = await specKitDockerService.validateProjectReadiness(projectId);
      setProjectReadiness(readiness);
      
      logger.info('SpecKit dashboard initialized successfully', { 
        projectId, 
        specKitAvailable: specKitStatus.speckit_available,
        ready: readiness.ready 
      });
    } catch (error) {
      logger.error('Failed to initialize SpecKit dashboard', { error, projectId });
      setSnackbarMessage('Failed to connect to SpecKit service');
      setSnackbarOpen(true);
    }
  };

  // Progress callback for workflow execution
  const handleWorkflowProgress = useCallback((phase: string, result: SpecKitCommandResult) => {
    setWorkflowProgress(prev => 
      prev.map(item => 
        item.phase === phase 
          ? { 
              ...item, 
              status: 'completed' as const, 
              result,
              endTime: new Date(),
              duration: item.startTime ? Date.now() - item.startTime.getTime() : 0
            }
          : item
      )
    );
  }, []);

  // Initialize SpecKit project
  const handleInitializeProject = async () => {
    if (projectInitialized) {
      setSnackbarMessage('Project already initialized');
      setSnackbarOpen(true);
      return;
    }

    try {
      logger.info('Initializing SpecKit project', { projectId, projectName });
      
      const initResult = await specKitDockerService.initializeProject(projectName);
      
      if (initResult.success) {
        setProjectInitialized(true);
        setSnackbarMessage('Project initialized successfully');
        setSnackbarOpen(true);
        
        // Update the first step as completed
        setWorkflowProgress(prev => 
          prev.map(item => 
            item.phase === 'init' 
              ? { 
                  ...item, 
                  status: 'completed' as const,
                  result: {
                    success: true,
                    message: initResult.message,
                    command: 'init',
                    output: `Project ${projectName} initialized successfully`,
                    generated_at: new Date().toISOString(),
                    files_generated: initResult.files_generated || []
                  },
                  endTime: new Date()
                }
              : item
          )
        );
      } else {
        throw new Error(initResult.message || 'Failed to initialize project');
      }
    } catch (error) {
      logger.error('Failed to initialize SpecKit project', { error, projectId });
      setSnackbarMessage(`Failed to initialize project: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSnackbarOpen(true);
    }
  };

  // Execute complete workflow
  const handleRunCompleteWorkflow = async () => {
    if (!projectReadiness?.ready) {
      setSnackbarMessage('Project not ready for workflow execution');
      setSnackbarOpen(true);
      return;
    }

    if (!projectInitialized) {
      setSnackbarMessage('Please initialize the project first');
      setSnackbarOpen(true);
      return;
    }

    setIsRunning(true);
    setWorkflowProgress(prev => prev.map(item => ({ ...item, status: 'pending' as const })));

    try {
      logger.info('Starting complete SpecKit workflow', { projectId });

      // Execute each workflow step sequentially
      for (const step of workflowSteps.slice(1)) { // Skip 'init' step
        try {
          setWorkflowProgress(prev => 
            prev.map(item => 
              item.phase === step.key 
                ? { ...item, status: 'running' as const, startTime: new Date() }
                : item
            )
          );

          const result = await specKitDockerService.executeCommand(
            projectName, 
            step.command,
            {
              requirements: step.key === 'constitution' ? requirements : undefined,
              user_stories: step.key === 'specify' ? userStories : undefined
            }
          );

          setWorkflowProgress(prev => 
            prev.map(item => 
              item.phase === step.key 
                ? { 
                    ...item, 
                    status: 'completed' as const, 
                    result,
                    endTime: new Date(),
                    duration: item.startTime ? Date.now() - item.startTime.getTime() : 0
                  }
                : item
            )
          );

          // Brief pause between steps
          await new Promise(resolve => setTimeout(resolve, 1000));

        } catch (stepError) {
          logger.error(`Failed to execute step ${step.key}`, { stepError, projectId });
          
          setWorkflowProgress(prev => 
            prev.map(item => 
              item.phase === step.key 
                ? { 
                    ...item, 
                    status: 'error' as const, 
                    error: stepError instanceof Error ? stepError.message : 'Unknown error',
                    endTime: new Date()
                  }
                : item
            )
          );
          break; // Stop workflow on first error
        }
      }

      setSnackbarMessage('Workflow execution completed');
      setSnackbarOpen(true);
      logger.info('Complete SpecKit workflow completed successfully', { projectId });

    } catch (error) {
      logger.error('Failed to run complete SpecKit workflow', { error, projectId });
      setSnackbarMessage(`Workflow failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSnackbarOpen(true);
    } finally {
      setIsRunning(false);
    }
  };

  // Execute individual phase
  const handleRunIndividualPhase = async (phase: string) => {
    if (phase === 'init') {
      return handleInitializeProject();
    }

    if (!projectInitialized) {
      setSnackbarMessage('Please initialize the project first');
      setSnackbarOpen(true);
      return;
    }

    const step = workflowSteps.find(s => s.key === phase);
    if (!step) {
      setSnackbarMessage(`Unknown phase: ${phase}`);
      setSnackbarOpen(true);
      return;
    }

    setWorkflowProgress(prev => 
      prev.map(item => 
        item.phase === phase 
          ? { ...item, status: 'running' as const, startTime: new Date() }
          : item
      )
    );

    try {
      const result = await specKitDockerService.executeCommand(
        projectName, 
        step.command,
        {
          requirements: step.key === 'constitution' ? requirements : undefined,
          user_stories: step.key === 'specify' ? userStories : undefined
        }
      );

      setWorkflowProgress(prev => 
        prev.map(item => 
          item.phase === phase 
            ? { 
                ...item, 
                status: 'completed' as const, 
                result,
                endTime: new Date(),
                duration: item.startTime ? Date.now() - item.startTime.getTime() : 0
              }
            : item
        )
      );

      setSnackbarMessage(`Phase ${step.label} completed successfully`);
      setSnackbarOpen(true);
      logger.info(`SpecKit phase ${phase} completed successfully`, { projectId });

    } catch (error) {
      logger.error(`Failed to run SpecKit phase ${phase}`, { error, projectId });
      
      setWorkflowProgress(prev => 
        prev.map(item => 
          item.phase === phase 
            ? { 
                ...item, 
                status: 'error' as const, 
                error: error instanceof Error ? error.message : 'Unknown error',
                endTime: new Date()
              }
            : item
        )
      );

      setSnackbarMessage(`Phase ${step.label} failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSnackbarOpen(true);
    }
  };

  // Helper function to get next phase
  const getNextPhase = (currentPhase: string): string => {
    const currentIndex = workflowSteps.findIndex(step => step.key === currentPhase);
    return currentIndex < workflowSteps.length - 1 ? workflowSteps[currentIndex + 1].key : '';
  };

  // Export results
  const handleExportResults = (format: 'markdown' | 'json') => {
    const completedResults = workflowProgress
      .filter(item => item.result)
      .reduce((acc, item) => ({ ...acc, [item.phase]: item.result }), {});

    if (Object.keys(completedResults).length === 0) {
      setSnackbarMessage('No results to export');
      setSnackbarOpen(true);
      return;
    }

    let exported: string;
    if (format === 'json') {
      exported = JSON.stringify(completedResults, null, 2);
    } else {
      exported = `# SpecKit Project Results: ${projectName}\n\nGenerated: ${new Date().toISOString()}\n\n`;
      
      Object.entries(completedResults).forEach(([phase, result]: [string, any]) => {
        const step = workflowSteps.find(s => s.key === phase);
        exported += `## ${step?.label || phase}\n\n`;
        exported += `**Command:** ${result.command}\n`;
        exported += `**Generated:** ${result.generated_at}\n`;
        exported += `**Status:** ${result.success ? 'Success' : 'Failed'}\n\n`;
        exported += `### Output\n\n\`\`\`\n${result.output}\n\`\`\`\n\n`;
        if (result.files_generated && result.files_generated.length > 0) {
          exported += `### Generated Files\n\n`;
          result.files_generated.forEach((file: string) => {
            exported += `- ${file}\n`;
          });
          exported += '\n';
        }
        exported += '---\n\n';
      });
    }
    
    const blob = new Blob([exported], {
      type: format === 'json' ? 'application/json' : 'text/markdown'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `speckit-results-${projectName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.${format === 'json' ? 'json' : 'md'}`;
    a.click();
    URL.revokeObjectURL(url);

    setSnackbarMessage(`Results exported as ${format.toUpperCase()}`);
    setSnackbarOpen(true);
  };

  // Add requirement
  const handleAddRequirement = () => {
    if (newRequirement.trim()) {
      setRequirements(prev => [...prev, newRequirement.trim()]);
      setNewRequirement('');
    }
  };

  // Add user story
  const handleAddUserStory = () => {
    if (newUserStory.trim()) {
      setUserStories(prev => [...prev, newUserStory.trim()]);
      setNewUserStory('');
    }
  };

  // Render status indicator
  const renderStatusIndicator = (status: 'pending' | 'running' | 'completed' | 'error') => {
    switch (status) {
      case 'pending':
        return <Chip size="small" label="Pending" />;
      case 'running':
        return <Chip size="small" label="Running" color="primary" icon={<RefreshIcon />} />;
      case 'completed':
        return <Chip size="small" label="Completed" color="success" icon={<CheckIcon />} />;
      case 'error':
        return <Chip size="small" label="Error" color="error" icon={<ErrorIcon />} />;
      default:
        return null;
    }
  };

  // Render content viewer for SpecKit command results
  const renderContentViewer = (result: SpecKitCommandResult) => {
    return (
      <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="between" mb={3}>
          <Typography variant="h5" gutterBottom>
            SpecKit Command Result: {result.command}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              label={result.success ? 'SUCCESS' : 'FAILED'}
              color={result.success ? 'success' : 'error'}
              variant="filled"
            />
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Generated: {result.generated_at}
        </Typography>

        {result.message && (
          <Alert severity={result.success ? 'success' : 'error'} sx={{ mb: 3 }}>
            {result.message}
          </Alert>
        )}

        {/* Command Output */}
        {result.output && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Command Output
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: theme.palette.grey[50] }}>
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  margin: 0,
                  maxHeight: '400px',
                  overflow: 'auto'
                }}
              >
                {result.output}
              </Box>
            </Paper>
          </Box>
        )}

        {/* Generated Files */}
        {result.files_generated && result.files_generated.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Generated Files ({result.files_generated.length})
            </Typography>
            <List dense>
              {result.files_generated.map((file, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <DocumentIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={file}
                    primaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>
    );
  };

  if (!status) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={4}>
            <LinearProgress sx={{ width: '100%', maxWidth: 300 }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="between" alignItems="center">
            <Box>
              <Typography variant="h4" gutterBottom>
                <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Enhanced SpecKit Dashboard
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                AI-Powered Project Management for {projectName}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Refresh Status">
                <IconButton onClick={initializeDashboard}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Results">
                <IconButton onClick={() => handleExportResults('markdown')}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Status Cards */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6">Service Status</Typography>
                  <Chip 
                    label={status.speckit_available ? 'Available' : 'Unavailable'}
                    color={status.speckit_available ? 'success' : 'error'}
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6">Service Health</Typography>
                  <Chip 
                    label={status.status}
                    color={status.status === 'healthy' ? 'success' : 'warning'}
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6">Project Status</Typography>
                  <Chip 
                    label={projectInitialized ? 'Initialized' : 'Not Initialized'}
                    color={projectInitialized ? 'success' : 'default'}
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6">Completed Steps</Typography>
                  <Typography variant="body2">
                    {workflowProgress.filter(p => p.status === 'completed').length} / {workflowSteps.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Project Readiness */}
      {projectReadiness && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Project Readiness</Typography>
            
            {projectReadiness.ready ? (
              <Alert severity="success">
                <AlertTitle>Ready for SpecKit Generation</AlertTitle>
                Project is configured and ready for AI-powered generation.
              </Alert>
            ) : (
              <Alert severity="warning">
                <AlertTitle>Configuration Required</AlertTitle>
                <List dense>
                  {projectReadiness.issues.map((issue, index) => (
                    <ListItem key={index}>
                      <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                      <ListItemText primary={issue} />
                    </ListItem>
                  ))}
                </List>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Recommendations:</Typography>
                <List dense>
                  {projectReadiness.recommendations.map((rec, index) => (
                    <ListItem key={index}>
                      <ListItemIcon><InfoIcon color="info" /></ListItemIcon>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Workflow Control */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Workflow Control</Typography>
              
              {/* Requirements Input */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Requirements</Typography>
                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Add requirement..."
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddRequirement()}
                  />
                  <Button onClick={handleAddRequirement} size="small">Add</Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {requirements.map((req, index) => (
                    <Chip
                      key={index}
                      label={req}
                      size="small"
                      onDelete={() => setRequirements(prev => prev.filter((_, i) => i !== index))}
                    />
                  ))}
                </Box>
              </Box>

              {/* User Stories Input */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>User Stories</Typography>
                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Add user story..."
                    value={newUserStory}
                    onChange={(e) => setNewUserStory(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddUserStory()}
                  />
                  <Button onClick={handleAddUserStory} size="small">Add</Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {userStories.map((story, index) => (
                    <Chip
                      key={index}
                      label={story}
                      size="small"
                      onDelete={() => setUserStories(prev => prev.filter((_, i) => i !== index))}
                    />
                  ))}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box display="flex" gap={1} flexDirection="column">
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={handleRunCompleteWorkflow}
                  disabled={!projectReadiness?.ready || isRunning || !projectInitialized}
                  fullWidth
                >
                  {isRunning ? 'Running Workflow...' : 'Run Complete Workflow'}
                </Button>
                
                {!projectInitialized && (
                  <Button
                    variant="outlined"
                    startIcon={<ScienceIcon />}
                    onClick={handleInitializeProject}
                    disabled={isRunning}
                    fullWidth
                  >
                    Initialize Project First
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExportResults('markdown')}
                  disabled={workflowProgress.every(item => !item.result)}
                  fullWidth
                >
                  Export Results
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Workflow Steps */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Workflow Steps</Typography>
              
              <Stepper orientation="vertical">
                {workflowSteps.map((step, index) => {
                  const progress = workflowProgress.find(p => p.phase === step.key);
                  
                  return (
                    <Step key={step.key} active={progress?.status === 'running'}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box display="flex" alignItems="center" gap={1}>
                            {step.icon}
                            {renderStatusIndicator(progress?.status || 'pending')}
                          </Box>
                        )}
                      >
                        <Box display="flex" justifyContent="between" alignItems="center" width="100%">
                          <Box>
                            <Typography variant="subtitle1">{step.label}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {step.description}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant={step.key === 'init' ? 'contained' : 'outlined'}
                            startIcon={step.key === 'init' ? <ScienceIcon /> : <PlayIcon />}
                            onClick={() => handleRunIndividualPhase(step.key)}
                            disabled={isRunning || (step.key !== 'init' && !projectInitialized)}
                          >
                            {step.key === 'init' ? 'Initialize' : 'Run'}
                          </Button>
                        </Box>
                      </StepLabel>
                      
                      <StepContent>
                        {progress?.status === 'running' && (
                          <LinearProgress sx={{ my: 2 }} />
                        )}
                        
                        {progress?.error && (
                          <Alert severity="error" sx={{ my: 2 }}>
                            {progress.error}
                          </Alert>
                        )}
                        
                        {progress?.result && (
                          <Box sx={{ mt: 2 }}>
                            <Button
                              size="small"
                              onClick={() => setSelectedResult(progress.result || null)}
                            >
                              View Result
                            </Button>
                            {progress.duration && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                Completed in {Math.round(progress.duration / 1000)}s
                              </Typography>
                            )}
                          </Box>
                        )}
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Result Viewer Dialog */}
      <Dialog
        open={Boolean(selectedResult)}
        onClose={() => setSelectedResult(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>SpecKit Result Viewer</DialogTitle>
        <DialogContent>
          {selectedResult && renderContentViewer(selectedResult)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedResult(null)}>Close</Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (selectedResult) {
                const content = selectedResult.output || JSON.stringify(selectedResult, null, 2);
                const blob = new Blob([content], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `speckit-${selectedResult.command}-${new Date().toISOString().split('T')[0]}.txt`;
                a.click();
                URL.revokeObjectURL(url);
              }
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for user feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        action={
          <Button color="inherit" size="small" onClick={() => setSnackbarOpen(false)}>
            Close
          </Button>
        }
      />
    </Box>
  );
};

export default EnhancedSpecKitDashboard;